"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"
import { cn } from "../../lib/utils"
import { moonUIButtonProVariants } from "./button"
import type { ButtonProps } from "./button"

const MoonUIPaginationPro = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
)
MoonUIPaginationPro.displayName = "PaginationPro"

const MoonUIPaginationContentPro = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
))
MoonUIPaginationContentPro.displayName = "PaginationContentPro"

const MoonUIPaginationItemPro = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
))
MoonUIPaginationItemPro.displayName = "PaginationItemPro"

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">

const MoonUIPaginationLinkPro = ({
  className,
  isActive,
  size = "icon",
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      moonUIButtonProVariants({
        variant: isActive ? "outline" : "ghost",
        size,
      }),
      className
    )}
    {...props}
  />
)
MoonUIPaginationLinkPro.displayName = "PaginationLinkPro"

const MoonUIPaginationPreviousPro = ({
  className,
  ...props
}: React.ComponentProps<typeof MoonUIPaginationLinkPro>) => (
  <MoonUIPaginationLinkPro
    aria-label="Go to previous page"
    size="md"
    className={cn("gap-1 pl-2.5", className)}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
    <span>Previous</span>
  </MoonUIPaginationLinkPro>
)
MoonUIPaginationPreviousPro.displayName = "PaginationPreviousPro"

const MoonUIPaginationNextPro = ({
  className,
  ...props
}: React.ComponentProps<typeof MoonUIPaginationLinkPro>) => (
  <MoonUIPaginationLinkPro
    aria-label="Go to next page"
    size="md"
    className={cn("gap-1 pr-2.5", className)}
    {...props}
  >
    <span>Next</span>
    <ChevronRight className="h-4 w-4" />
  </MoonUIPaginationLinkPro>
)
MoonUIPaginationNextPro.displayName = "PaginationNextPro"

const MoonUIPaginationEllipsisPro = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
MoonUIPaginationEllipsisPro.displayName = "PaginationEllipsisPro"


// Internal aliases for Pro component usage
export const PaginationInternal = MoonUIPaginationPro
export const PaginationContentInternal = MoonUIPaginationContentPro
export const PaginationItemInternal = MoonUIPaginationItemPro
export const PaginationLinkInternal = MoonUIPaginationLinkPro
export const PaginationPreviousInternal = MoonUIPaginationPreviousPro
export const PaginationNextInternal = MoonUIPaginationNextPro
export const PaginationEllipsisInternal = MoonUIPaginationEllipsisPro

// Pro exports  
export { MoonUIPaginationPro, MoonUIPaginationContentPro, MoonUIPaginationItemPro, MoonUIPaginationLinkPro, MoonUIPaginationPreviousPro, MoonUIPaginationNextPro, MoonUIPaginationEllipsisPro }

// Clean exports (without MoonUI prefix for easier usage)
export { MoonUIPaginationPro as Pagination, MoonUIPaginationContentPro as PaginationContent, MoonUIPaginationItemPro as PaginationItem, MoonUIPaginationLinkPro as PaginationLink, MoonUIPaginationPreviousPro as PaginationPrevious, MoonUIPaginationNextPro as PaginationNext, MoonUIPaginationEllipsisPro as PaginationEllipsis }